/*
 * lesson editing-context & state for the lesson currently being edited
 */
import React, { createContext, useState, useContext, useEffect, useRef } from 'react';

import { lessonTypeMap, lessonTypes, typeDefMap, elements, nodeAddTypes, pageTypeDefs } from '../defines/lessondefs';
import {
    buildNode,
    insertNewNode,
    moveNode,
    removeNode,
    deflateNode,
    lessonFor,
    lessonNodeMap,
    ancestorsFor,
    fieldsByName,
    duplicateNode,
    wireNodeMap,
    deflateNodeTree,
    templateDocs,
    lessonMap,
    exportNode,
    conformToFigmaLayout,
    getFigmaTemplates,
    checkAndRepairLessonTree
} from '../libs/lessonLib';
import httpAPI from '../apis/http';
import { apiPaths, apiServer } from '@mirinae/shared/defines/paths';
import { lessonsPaths, explorerPaths, membersPaths, authPaths} from '@mirinae/shared/defines/paths';
import { myLessonsAPI } from "../../../modules/apis/myLessons";
import { colorChoices } from "../defines/style";

const EditorContext = createContext({});

export const groupChoices = ['Starter', 'Beginner', 'Intermediate', 'Advanced', 'Idiom', 'Neologism', 'Proverb',];
export const statusChoices = [
    'Planned', 'Specifying', 'Ready for edit', 'Editing', 'Ready for review', 'Reviewing',
    'Ready for test', 'Testing', 'Final review', 'Template', 'Coming soon', 'Published']
export const statusSelectOptions = (user, lessonNode) => (
    statusChoices.map(c => ({
        label: c, value: c,
        disabled: (
            lessonNode.status.match(/Published|Coming soon/) && !user.role.match(/admin|superadmin|superuser/i))
            || disabledStatusChoice(user, c) }))
);
export const disabledStatusChoice = (user, choice) => {
    return user.role.match(/admin|superadmin|superuser/i)
        ? false
        : (user.role === 'lessoneditor'
            ? ['Planned', 'Specifying', 'Ready for edit', 'Template', 'Coming soon', 'Published'].includes(choice)
            : (user.role === 'lessoncreator'
                ? ['Coming soon', 'Published'].includes(choice)
                : !['Testing', 'Final review'].includes(choice)));
};

const editorStateInit = {
    figmaPageID: null,
    lessonID: null,
    figmaFileKey: '',
    figmaFileName: '',
    figmaPages: [],

    type: '',
    selectedPattern: null,
    sections: [],
    selectedSection: null,

    modes: ['step', 'autoplay', 'extra'],
    lessonTree: {
        nodeID: 'root',
        typeDefPathName: 'root',
        nodeClass: 'root',
        typeDef: { typeMap: lessonTypeMap, lessons: lessonTypes },
        children: [],
    },
    treeContextMenu: {
        mouseX: 0, mouseY: 0,
        show: false,
        node: null,
        e: null,
    },

    // dialog state (replicated in lessons context (for now for lessons admin mode)
    deletingNode: null,

    authenticated: process.env.REACT_APP_MODE.startsWith('LOCAL'), // otherwise require login

    snapshotVersion: '',

    animationEditor: {
        show: false,
        element: null,
        showCommandsFor: false,
    },
    animContextMenu: {
        open: false,
        context: {},
    },
    lessonSpecBox: {
        show: false,
    },
    specContextMenu: {
        open: false,
        context: {},
    },

    clipboard: null,
    clipboardTimestamp: 0,
};

// DB nodeMap updates need to be serialized to avoid out-of-order state corruption
const dbUpdateQueue = [];
let dbQueueCount = 0;
let templateLessons = [];

export const useEditorContext = ([userContext], global, selection) => {
    const [editorState, setEditorState] = useState(editorStateInit);
    let navigate;
    let dialog;
    const updatePlayerOnEdit = useRef(true);

    useEffect(() => {
        // grab the server-side clipboard on startup
        if (userContext.user && !editorState.clibboard)
            getServerSideClipboard();
    }, [userContext.user]);

    useEffect(() => {
        const loadLessonList = async () => {
            global.update({ showEditor: true });
            // load template lesson objects
            let lessons = global.templateLessons;
            if (lessons.length === 0) {
                const response = await myLessonsAPI.lessonList({ });
                lessons = response.data.lessons.filter(lesson => lesson.lessonNode.status === 'Template');
            }
            await lessons.forEach(async tl => {
                const { lesson = {} } = await httpAPI(apiServer, apiPaths.lesson, { data: { lessonID:tl._id } });
                if (lesson) {
                    tl.nodeMap = lesson.nodeMap;
                    wireNodeMap(tl.nodeMap);
                }
            });
            templateLessons = lessons;
        };
        loadLessonList();
    }, []);

    const endEditLesson = async (lessonID) => {
        console.log('editor.endEditLesson for', userContext.user?.name)
        if (userContext.user) {
            try {
                await httpAPI('', apiPaths.editLock, { data: { lessonID, userID: userContext.user && userContext.user._id, action: 'release' } });
            } catch (e) {
            }
        }
    };

    const queueNodeMapUpdate = async (update, options) => {
        dbUpdateQueue.push(update);
        dbQueueCount += 1;
        if (dbQueueCount === 1) {
            dialog.setShowAlert(true,  'Saving changes, please wait...', 3000, true);
            while (dbUpdateQueue.length > 0) {
                console.log(dbUpdateQueue.length, dbQueueCount);
                const user = userContext.user ? { name: userContext.user.name, _id: userContext.user._id } : {};
                const data = { data: { updates: dbUpdateQueue.splice(0).map(d => d.data), user, ...(options || {}) } };
                // data.data.nodes.filter(n => n.nodeClass === 'section').forEach(n => console.log('section db update', n));
                try {
                    const response = await httpAPI(apiServer, apiPaths.updateNodeMapByGuid, data);
                    dbQueueCount -= data.data.updates.length;
                    // returns mongo objects for any new Lesson objects, update local node copies with server-updated fields
                    response.lessonObjects.forEach(({ lessonObject, node }) => {
                        const lesson = lessonNodeMap(node)[node.nodeID];
                        if (lesson && lesson.nodeClass === 'lesson') {
                            lesson.lessonObjectID = lessonObject._id;
                            delete lessonObject.nodeMap; // keep the local nodeMap, it contains well-formed nodes already
                            Object.assign(lessonMap[lesson.lessonGuid], lessonObject);
                            // switch player to just created lesson
                            const pageURL = makePageURL(lessonObject._id);
                            navigate(pageURL, { replace: true });
                        }
                    });
                } catch (e) {
                    dialog.setShowAlert(false);
                    dialog.setShowAlert(true, `Database update failed: ${e}`);
                    console.log(`\n**** Database update failed: ${e}\n`);
                    dbQueueCount -= data.data.updates.length;
                    return;
                }
            }
            dialog.setShowAlert(false);
            console.log('db queue empty', dbUpdateQueue.length, dbQueueCount);
            dbQueueCount = 0;
        }
    };

    const updateEditorState = (update) => {
        // console.log('update edit context, in editor', update);
        setEditorState((prev) => ({ ...prev, ...update }));
    };

    const refreshLessonTree = (full) => {
        updateEditorState({ lessonTree: editorState.lessonTree });
        if (updatePlayerOnEdit.current) {
            global.update({ playerUpdateTrigger: global.playerUpdateTrigger + 1 });
        }
    };

    const updateNodeField = (node, field, value) => {
        node[field] = value;
        refreshLessonTree();
    };

    const editElementFields = (node, edit, field) => {
        // element node field array has been restructuredm update everyone
        refreshLessonTree();
    };

    const updateElementField = (node, field, prop, value) => {
        field[prop] = value;
        refreshLessonTree();
    };

    const fieldValue = (element, field) => {
        // get named field value from element
        const f = fieldsByName(element, field);
        if (f.src) {
            // indirect reference, retrieve value from specified source object & prop
            const [obj, prop] = f.src.split('.');
            return ancestorsFor(element)[obj][prop];
        } else {
            return f.value;
        }
    };

    const updateElementFieldPart = (node, field, pn, i, subfield, value) => {
        field.parts[pn][i][subfield] = value;
        refreshLessonTree();
    };

    const updateElementFieldParts = (node, field, pn, newParts) => {
        field.parts[pn] = [...newParts];
        refreshLessonTree();
    };

    const updateElementListField = (node, field, items) => {
        field.listItems = [...items];
        refreshLessonTree();
    };

    const updateElementListFieldItem = (node, field, itemIndex, fieldName, value) => {
        field.listItems[itemIndex][fieldName] = value;
        refreshLessonTree();
    };

    const commitNodeUpdate = (node, dontCloseAlert) => {
        // commit node changes to the DB
        if (node.nodeID !== 'root') {
            const data = { data: { figmaFileKey: editorState.figmaFileKey, nodes: [{ ...deflateNode(node) }] } };
            queueNodeMapUpdate(data, { dontCloseAlert });
        }
    };

    const clear = () => setEditorState({ ...editorStateInit });

    const closeTreeContextMenu = () => updateEditorState({ treeContextMenu: {...editorStateInit.treeContextMenu }});

    // ----- tree contextMenu definitions & handlers -----

    const deleteNode = (node, e) => {
        if (e.metaKey)
            removeNodesAndUpdateDB([node])
        else {
            updateEditorState({ deletingNode: node });
            dialog.update({ showDeleteConfirm: true, onDeleteConfirmed: () => deleteConfirmed(node), deletingNodeDescr: `${node.nodeClass} "${node.title}"` });
        }
    };

    const deleteConfirmed = (node) => {
        removeNodesAndUpdateDB([node]); // [editorState.deletingNode]);
        if (node.nodeClass === 'lesson') {
            // drop our to lessons list if we currently open less
            navigate(`${lessonsPaths.lessons}`, { replace: true });
        }
    };

    const removeNodesAndUpdateDB = (nodes) => {
        // remove nodes & gather changes
        const changedNodes = [];
        const deletedNodes = [];
        nodes.forEach(node => {
            const [changes, deletes] = removeNode(node);
            changes.forEach(c => { if (!changedNodes.includes(c)) changedNodes.push(c); });
            deletes.forEach(d => { if (!deletedNodes.includes(d)) deletedNodes.push(d); });
        });
        // update DB
        const data = { data: {
            figmaFileKey: editorState.figmaFileKey,
            nodes: changedNodes.filter(n => n.nodeID !== 'root').map(n => ({ ...deflateNode(n)})),
            deletedNodes: deletedNodes.filter(n => n.nodeID !== 'root').map(n => ({ ...deflateNode(n)})),
        }};
        queueNodeMapUpdate(data);
        //
        updateEditorState({ deletingNode: null });
        refreshLessonTree();
        checkLessonTree('removeNodesAndUpdateDB');
    };

    const createNewMode = (node) => {
        console.log('adding a new mode not yet implemented for', node.title);
    };

    const addNode = (nodeClass, typeDef, parent) => {
        if (nodeClass === 'lesson') {
            dialog.setShowAlert(true, 'Adding new lesson, please wait...', 0, true);
        }
        const [nodesToSave, node] = buildNode(nodeClass, typeDef, parent);
        updateDBNodeMap([...nodesToSave]);
        refreshLessonTree();
        selection.selectAndShow(node, { showInPlayer: true });
        checkLessonTree('addNode');
    };

    const addElement = (elementTypeDef, refNode) => {
        const parent = refNode.nodeClass === 'step' ? refNode : refNode.parent;
        const newNode = buildNode('element', elementTypeDef, parent);
        commitNodeUpdate(parent);
        refreshLessonTree();
        checkLessonTree('addElement');
        return newNode;
    };

    // insert element in a container element
    const insertSubElement = (elementTypeDef, refNode) => {
    };

    const insertNode = (nodeClass, typeDef, offset, refNode) => {
        const parent = refNode.parent;
        const newNode = insertNewNode(nodeClass, typeDef, refNode, offset, selection);
        const [nodesToSave, node] = buildNode(nodeClass, typeDef, parent, newNode);
        updateDBNodeMap([...nodesToSave, ...parent.children]);
        refreshLessonTree();
        selection.selectAndShow(node, { showInPlayer: true });
        checkLessonTree('insertNode');
    };

    const insertElement = (elementTypeDef, offset, refNode) => { // not used for now, just adding elements to the end of the current step
        const parent = refNode.parent;
        const newNode = insertNewNode('element', elementTypeDef, refNode, offset, selection);
        const [nodesToSave, node] = buildNode('element', elementTypeDef, parent, newNode);
        updateDBNodeMap([...nodesToSave]);
        refreshLessonTree();
        checkLessonTree('insertElement');
    };

    const addSelectedFigmaGraphic = (refNode) => {
        // custom graphic elements have their own custom typeDef, only support single text or frame elements for now
        const selection = selection.figmaSelection;
        if (selection.figmaSelectedLesson &&
            selection.length === 1 &&
            !selection[0].nodeID &&
            selection[0].figmaType.match(/TEXT|FRAME|RECTANGLE/))
        {
            const customTypeDefPath = `custom-${typeDefMap.length + 1}`;
            const customTypeDef = {
                title: `custom`,
                type: `custom-${selection[0].figmaType.toLowerCase()}`,
                typeDefPath: customTypeDefPath,
                customFigmaNodeID: selection[0].figmaNodeID,
            };
            typeDefMap[customTypeDefPath] = customTypeDef;
            addElement(customTypeDef, refNode);
        }
    };

    // duplicate given node
    const duplicate = (node, e) => {
        if (node.nodeClass.match(/lesson|section/)) dialog.setShowAlert(true,  'Duplicating, please wait...', 0, true);
        const [newNode, nodesToSave] = duplicateNode(node,  { title: `${node.title} copy` });
        updateDBNodeMap([...nodesToSave]);
        refreshLessonTree();
        selection.selectAndShow(newNode, { showInPlayer: true });
        checkLessonTree('duplicate');
    };

    // save node to (internal) copy/paste clipboard & write deflated node-tree as text to users server-side clipboard
    const copy = (node) => {
        updateEditorState({ clipboard: node, clipboardTimestamp: Date.now() });
        const data = { data: { userID: userContext.user._id, nodeStruct: deflateNodeTree(node) } };
        httpAPI(apiServer, apiPaths.saveClipboard, data);
    };

    const pasteLabel = (node) => {
        return editorState.clipboard ? `Paste ${editorState.clipboard.nodeClass}` : 'Paste';
    };

    const pasteMismatch = (node) => {
        return !(editorState.clipboard &&
            (editorState.clipboard.nodeClass === node.nodeClass ||
                editorState.clipboard.parent && editorState.clipboard.parent.nodeClass === node.nodeClass && node.children.length > 0));
    };

    const paste = async (node, e) => {
        let clipboard = editorState.clipboard;
        if (!clipboard || Date.now() - editorState.clipboardTimestamp > 5000) {
            try {
                const response = await httpAPI(apiServer, apiPaths.getClipboard, { data: { userID: userContext.user._id } } );
                const obj = response.nodeStruct;
                if (obj.root && (!clipboard || obj.timestamp > editorState.clipboardTimestamp)) {
                    wireNodeMap(obj.nodeMap);
                    const node = obj.nodeMap[obj.root];
                    node.parent = null;
                    updateEditorState({ clipboard: node, clipboardTimestamp: obj.timestamp });
                    clipboard = node;
                }
            } catch (e) { } // use current clipboard if any
        }
        if (clipboard) {
            let newNode, nodesToSave;
            if (node.nodeClass.match(/lesson|section/)) dialog.setShowAlert(true,  'Pasting, please wait...', 0, true);
            if (clipboard.nodeClass === node.nodeClass) {
                 [newNode, nodesToSave] = duplicateNode(clipboard, {}, false, node.parent, node.index);
            } else if (clipboard.parent && clipboard.parent.nodeClass === node.nodeClass && node.children.length > 0) {
                [newNode, nodesToSave] = duplicateNode(clipboard, {}, false, node, node.children.length - 1);
            }
            updateDBNodeMap([...nodesToSave]);
            refreshLessonTree();
            selection.selectAndShow(newNode, { showInPlayer: true });
            checkLessonTree('paste');
        }
    };

    const backupLesson = (lesson) => {
        backupOneLesson(lesson);
        dialog.setShowAlert(true, 'Backup completed');
    };

    // export as downloadable JSON file
    const dowloadNode = (node) => exportNode(node);

    const importLesson = (node) => importNode('lesson', node);
    const importSection = (node) => importNode('section', node);
    const importPage = (node) => importNode('page', node);

    const importNode = (nodeClass, node) => {
        const input = document.getElementById('file-dialog-id');
        const loadImport = async (e) => {
            input.removeEventListener('input', loadImport);
            try {
                const json = await input.files[0].text();
                const nodeStruct = JSON.parse(json);
                if (nodeStruct.root && nodeStruct.nodeMap[nodeStruct.root].nodeClass === nodeClass) {
                    wireNodeMap(nodeStruct.nodeMap);
                    const importNode = nodeStruct.nodeMap[nodeStruct.root];
                    importNode.parent = null;
                    dialog.setShowAlert(true, 'Importing, please wait...', 0, true);
                    let newNode, nodesToSave;
                    if (nodeClass === 'lessons') {
                        [newNode, nodesToSave] = duplicateNode(importNode, {}, false, editorStateInit.lessonTree, editorStateInit.lessonTree.children.length - 1);
                    } else {
                        [newNode, nodesToSave] = duplicateNode(importNode, {}, false, node.parent, node.index);
                    }
                    updateDBNodeMap([...nodesToSave]);
                    refreshLessonTree();
                    selection.selectAndShow(newNode, { showInPlayer: true });
                } else {
                    dialog.setShowAlert(true, `imported file is not a ${nodeClass}, not imported`);
                }
            } catch (e) {
            }
        };
        input.addEventListener('input', loadImport);
        input.click();
    };

    const conformNode = (node) => {
        if (node.nodeClass.match(/lesson|section/)) dialog.setShowAlert(true,  'Conforming, please wait...', 0, true);
        const nodesToSave = new Set();
        conformToFigmaLayout(node, nodesToSave);
        if (nodesToSave.size > 0) {
            updateDBNodeMap([...nodesToSave], [], { action: 'conform', dontLogUpdate: true });
            refreshLessonTree();
        } else {
            dialog.setShowAlert(false);
        }
    };

    const addNodeFromTemplate = (templateNode, addToNode, e) => {
        let newNode, nodesToSave;
        if (templateNode.nodeClass === addToNode.nodeClass) {
             [newNode, nodesToSave] = duplicateNode(templateNode, {}, false, addToNode.parent, addToNode.index);
        } else if (templateNode.parent && templateNode.parent.nodeClass === addToNode.nodeClass && addToNode.children.length > 0) {
            [newNode, nodesToSave] = duplicateNode(templateNode, {}, false, addToNode, addToNode.children.length - 1);
        }
        updateDBNodeMap([...nodesToSave]);
        refreshLessonTree();
        selection.selectAndShow(newNode, { showInPlayer: true });
    };

    const typeChoices = (node, level) => {
        return typeDefMap[node.typeDefPathName][level].map(s => s.title || s.type).filter(c => !c.startsWith('--'));
    };

    const readable = (label) => [...label].map((c, i) => i === 0 ? c.toUpperCase() : (c.match(/[A-Z]/) ? ` ${c}` : c)).join('');

    const templateSubmenus = (nodeClass, handler, node) => (
        node ? (node.nodeClass === nodeClass ? { label: node.title, handler: (item, e) => handler(node, item, e) }
                : { label: node.title, submenu: node.children.map(c => templateSubmenus(nodeClass, handler, c)) })
        : templateLessons.filter(lesson => (nodeClass === 'page' && lesson.title.match(/page/i)) || (nodeClass !== 'page' && !lesson.title.match(/page/i)))
                .map(lesson => templateSubmenus(nodeClass, handler, lesson.nodeMap[lesson.lessonNode.nodeID]))
    );

    const makeInsertSubMenus = (node, level, nodeClass, offset) => [
        { label: `Built-in ${nodeClass}s`, submenu:
            Object.getOwnPropertyNames(nodeAddTypes[nodeClass]).sort().map(lessonType => (
                { label: readable(lessonType), submenu: Object.getOwnPropertyNames(nodeAddTypes[nodeClass][lessonType]).sort().map(pageType => (
                    { label: readable(pageType), handler: insertNode.bind(null, nodeClass, nodeAddTypes[nodeClass][lessonType][pageType], offset) })) })) },
        { label: `Template ${nodeClass}s`, submenu: templateSubmenus(nodeClass, (node, item, e) => addNodeFromTemplate(node, item, e)) }
    ];

    const makeAddSubMenus = (node, level, nodeClass) => [
        { label: `Built-in ${nodeClass}s`, submenu:
            Object.getOwnPropertyNames(nodeAddTypes[nodeClass]).sort().map(lessonType => (
                { label: readable(lessonType), submenu: Object.getOwnPropertyNames(nodeAddTypes[nodeClass][lessonType]).sort().map(pageType => (
                    { label: readable(pageType), handler: addNode.bind(null, nodeClass, nodeAddTypes[nodeClass][lessonType][pageType]) })) })) },
        { label: `Template ${nodeClass}s`, submenu: (() => templateSubmenus(nodeClass, (node, item, e) => addNodeFromTemplate(node, item, e)))() }
    ];

    const makeElementAddSubMenus = (node) => {
        // menu of all element type choices, arranged by group
        const elementChoices = [];
        const elementGroups = {};
        Object.values(elements).forEach(typeDef => {
            if (!elementGroups[typeDef.group])
                elementGroups[typeDef.group] = [];
            elementGroups[typeDef.group].push({ label: readable(typeDef.type), handler: addElement.bind(null, typeDef) });
        });
        const submenu =  Object.getOwnPropertyNames(elementGroups).sort().map(group => (
            { label: readable(group), submenu: elementGroups[group].sort((t1, t2) => t1.label < t2.label ? -1 : 1) }
        ));
        // add selected new graphic if any selected
        if (selection.figmaSelection.length === 1 && selection.figmaSelection[0].figmaType.match(/TEXT|FRAME|RECTANGLE/)) {
            submenu.push({ divider: true },);
            submenu.push({ label: 'Selected Figma graphic', handler: addSelectedFigmaGraphic });
        }
        return submenu;
    };

    const contextMenu = () => {
        if (!templateDocs.templatePages)
            return [ { label: 'Please try again in a few seconds...', handler: () => {} } ];  // no menus until template docs are loaded
        getServerSideClipboard();
        const node = editorState.treeContextMenu.node;
        switch (node.nodeClass) {
            case 'lesson':
                return [
                    { label: 'Add a new section to this lesson', submenu: makeAddSubMenus(node, 'sections', 'section') },
                    { label: 'Copy lesson', handler: copy },
                    { label: 'Download lesson', handler: dowloadNode },
                    { label: 'Upload lesson', handler: importLesson },
                    { label: pasteLabel, handler: paste, disableIf: pasteMismatch },
                    { label: 'Duplicate lesson', handler: duplicate },
                    { label: 'Backup lesson', handler: backupLesson },
                    { divider: true },
                    { label: 'Check this lesson', handler: repairLessonTree },
                    { label: 'Conform to Figma templates', handler: conformNode },
                ].concat(editorState.treeContextMenu.e && editorState.treeContextMenu.e.shiftKey ? [
                    { divider: true },
                    { label: 'Delete this lesson', handler: deleteNode },
                    ] : []);
            case 'section':
                return [
                    { label: 'Add a new page to this section', submenu: makeAddSubMenus(node, 'pages', 'page') },
                    { divider: true },
                    { label: 'Insert new section above', submenu: makeInsertSubMenus(node, 'sections', 'section', 0) },
                    { label: 'Insert new section below', submenu: makeInsertSubMenus(node, 'sections', 'section', 1) },
                    { label: 'Copy section', handler: copy },
                    { label: 'Download section', handler: dowloadNode },
                    { label: 'Upload section', handler: importSection },
                    { label: pasteLabel, handler: paste, disableIf: pasteMismatch },
                    { label: 'Duplicate section', handler: duplicate },
                    // { divider: true },
                    // { label: 'Create a new mode for this section', handler: createNewMode },
                    { divider: true },
                    { label: 'Delete this section', handler: deleteNode },
                ];
            case 'page':
                return [
                    // { label: 'Add new step', handler: addNode.bind(null, 'step', typeDefMap['step']) },
                    { divider: true },
                    { label: 'Insert new page above', submenu: makeInsertSubMenus(node, 'pages', 'page', 0) },
                    { label: 'Insert new page below', submenu: makeInsertSubMenus(node, 'pages', 'page', 1) },
                    { label: 'Copy page', handler: copy },
                    { label: 'Download page', handler: dowloadNode },
                    { label: 'Upload page', handler: importPage },

                    { label: pasteLabel, handler: paste, disableIf: pasteMismatch },
                    { label: 'Duplicate page', handler: duplicate },
                    { divider: true },
                    { label: 'Conform to Figma template', handler: conformNode },
                    { divider: true },
                    // { label: 'Create a new mode for this page', handler: createNewMode },
                    { label: 'Delete this page', handler: deleteNode },
                ];
            // element-level insertions not currently supported in embedded editor as only full-page insertions have
            // the necessary layout info in the figma template docs.  May add this back in if we provide layout editing
            // in the embedded editor
            case 'step':
                return [
                    // { label: 'Add new element', submenu: makeElementAddSubMenus(node) },
                    // { divider: true },
                    // { label: 'Insert new step above', handler: insertNode.bind(null, 'step', typeDefMap['step'], 0) },
                    // { label: 'Insert new step below', handler: insertNode.bind(null, 'step', typeDefMap['step'], 1) },
                    { label: 'Copy step', handler: copy },
                    { label: pasteLabel, handler: paste, disableIf: pasteMismatch },
                    { label: 'Duplicate step', handler: duplicate },
                    { divider: true },
                    // { label: 'Create a new mode', handler: createNewMode },
                    { label: 'Delete this step', handler: deleteNode },
                ];
            case 'element':
                return [
                    // { label: 'Insert new element above', submenu: makeInsertSubMenus(node, 'elements', 'element', 0) },    this would need a special makeInsertElementSubMenus, just add-to-end for now
                    // { label: 'Insert new element below', submenu: makeInsertSubMenus(node, 'elements', 'element', 1) },
                    // { label: 'Add new element', submenu: makeElementAddSubMenus(node) },
                    { label: 'Copy element', handler: copy },
                    { label: pasteLabel, handler: paste, disableIf: pasteMismatch },
                    { label: 'Duplicate element', handler: duplicate },
                    { divider: true },
                    { label: 'Conform to Figma template', handler: conformNode },
                    { divider: true },
                    // { label: 'Create a new mode', handler: createNewMode },
                    { label: 'Delete this element', handler: deleteNode },
                ];
        }
    };

    const handleContextMenuSelect = (item, event) => {
        item.handler(editorState.treeContextMenu.node, event);
        closeTreeContextMenu();
    };

    const disableContextMenu = (item) => item.disableIf && item.disableIf(editorState.treeContextMenu.node);

    // -------- Header menu ----------

    // requests the server check for missing Figma assets and gathers those that are missing
    const gatherFigmaAssets = () => {
        const lesson = selection.selectedNode && lessonFor(selection.selectedNode);
        if (lesson) {
            const data = { data: { lessonNode: deflateNode(lesson) } };
            httpAPI(apiServer, apiPaths.gatherFigmaAssets, data)
                .catch(e => {
                    dialog.setShowAlert(true, `gather-assets failed: ${e}`);
                });
        }
    };

    // checks for lesson-tree consistency
    const repairLessonTree = (lesson) => {
        dialog.setShowAlert(true,  'Checking lesson(s), please wait...', 0, true);
        const [fixes, changedNodes, deletedNodes] = checkAndRepairLessonTree(lesson);
        if (changedNodes.length > 0 || deletedNodes.length > 0) {
            // apply fixes
            dialog.setShowAlert(false);
            dialog.setShowAlert(true, `${fixes.join(', ')} - saving fixes...`);
            const data = {
                data: {
                    figmaFileKey: null,
                    nodes: changedNodes,
                    deletedNodes: deletedNodes,
                }
            };
            queueNodeMapUpdate(data, { action: 'repair' });
            refreshLessonTree();
        } else
            dialog.setShowAlert(true, 'No issues found');
    };

    // checks for lesson-tree consistency & reports issues
    const checkLessonTree = (location) => {
        const ppath = (node) => {
            const ancestors = ancestorsFor(node);
            return ['lesson', 'section', 'page'].filter(n => ancestors[n]).map(n => ancestors[n].title).join('/');
        };
        try {
            console.log('\nchecking lesson tree in ', location);
            // check nodeMap first
            Object.values(global.currentLessonObject.nodeMap).forEach(node => {
                if (typeof node.parent === "string") {
                    console.log(`   * unmapped parent ${node.parent} in node ${node.nodeID} (${ppath(node)}/${node.title})`);
                    if (!lessonNodeMap(node)[node.parent])
                        console.log(`       * ${node.parent} not also in nodeMap`);
                }
                if (node.children) node.children.filter(c => typeof c === "string").forEach(c => {
                    console.log(`   * unmapped child ${c} in node ${node.nodeID} (${ppath(node)}/${node.title})`);
                    if (!lessonNodeMap(node)[c])
                        console.log(`       * ${c} not in nodeMap`);
                });
            });
            // recurse through built tree
            const checkNode = (node, parent) => {
                // console.log('checking', node);
                // console.log('     ', ancestorsFor(node));
                const { lesson } = ancestorsFor(node);
                if (lesson && lesson.lessonGuid !== node.lessonGuid) {
                    // node in wrong lesson!  remove
                    console.log(`   * node in wrong lesson ${node.nodeID} (${ppath(node)}/${node.title}), should be in leeson with lessonGuid ${lesson.lessonGuid}, says it is in ${node.lessonGuid}`);
                }
                if (parent && node.parent !== parent) {
                    // parent-child inconsistent, repair in favor of parent
                    console.log(`   * node parent wrong ${node.parent.nodeID} - ${parent.nodeID} in ${node.nodeID} (${ppath(node)}/${node.title}), should be ${parent.nodeID}`);
                }
                if (node.fields)
                    node.fields.filter(f => f.src).forEach(f => {
                        // ensure fieldRefs exist for any field sources
                        const [obj, prop] = f.src.split('.');
                        const srcObj = ancestorsFor(node)[obj];
                        if (!srcObj.fieldRefs || !srcObj.fieldRefs.find(fr => fr.nodeID === node.nodeID && fr.fieldName === f.name)) {
                            console.log(`missing fieldRef in ${node.nodeID} - ${f.name}`);
                       }
                    });
                node.children && node.children.forEach((c, i) => {
                    if (typeof c === 'string') {
                        // unknown child!
                        console.log(`   * unknown child ${c} in ${node.nodeID} (${ppath(node)}/${node.title})`);
                    }
                });
                node.children && node.children.forEach((c, i) => {
                    if (typeof c !== 'string') {
                        if (c.index !== i) {
                            // index out of order!
                            console.log(`   * node index out of order for node ${node.nodeID} (${ppath(node)}/${node.title}), found ${node.index}, should be ${i}`);
                        }
                        checkNode(c, node);
                    }
                });
            };
            checkNode(editorStateInit.lessonTree);
        } catch (e) {
            console.log('** exception in checkLessonTree.', e);
        }
        console.log('lesson check done.');
    };

    const collapseNavigatorTree= () => {
        selection.update({ expandedNodes: [editorState.lessonTree] });
    };

    const backupOneLesson = (lesson) => {
        const backupDate = new Date().toJSON().slice(0, -5);
        const data = { data: {
            label: `${editorState.figmaFileName}:${lesson.title}.backup.${backupDate}`,
            file: editorState.figmaFileName,
            backupDate,
            title: lesson.title,
            nodeStruct: deflateNodeTree(lesson)
        } };
        httpAPI(apiServer, apiPaths.backup, data); // can be async
    };

    const backupAllLessons = () => {
        editorState.lessonTree.children.filter(n => !n.backupForNodeID).forEach(lesson => backupOneLesson(lesson));
        dialog.setShowAlert(true, 'Backup completed');
    };

    const showRestoreDialog = () => dialog.update({ showBackupDialog: true });

    const restoreBackups = (backups) => {
        if (backups.length > 0) {
            backups.forEach((backup, i) => {
                const data = { data: { id: backup._id } };
                httpAPI(apiServer, apiPaths.getBackup, data)
                    .then(response => {
                        dialog.setShowAlert(true,  'Restoring, please wait...', 0, true);
                        const { nodeStruct } = response.backup;
                        wireNodeMap(nodeStruct.nodeMap);
                        const node = nodeStruct.nodeMap[nodeStruct.root];
                        if (node.nodeClass === 'lesson') {
                            const [newNode, nodesToSave] = duplicateNode(node, {}, false, editorStateInit.lessonTree, editorStateInit.lessonTree.children.length - 1);
                            updateDBNodeMap([...nodesToSave]);
                            if (i === backups.length - 1) {
                                refreshLessonTree();
                                selection.selectAndShow(newNode, { showInPlayer: true });  // select & show last-restored in editor/plauer
                                dialog.setShowAlert(false);
                            }
                        }
                    })
            });
        }
    };

    const pasteLessons = () => {
        if (editorState.clipboard) {
            let lessons = [];
            if (Array.isArray(editorState.clipboard))
                lessons = [...editorState.clipboard];
            else if (editorState.clipboard.nodeClass === 'lesson')
                lessons = [editorState.clipboard];
            if (lessons.length > 0) {
                dialog.setShowAlert(true, 'Pasting, please wait...', 0, true);
                const pasteOneLesson = () => {
                    const lesson = lessons.pop();
                    const [newNode, nodesToSave] = duplicateNode(lesson, {}, false, editorStateInit.lessonTree, editorStateInit.lessonTree.children.length - 1);
                    updateDBNodeMap([...nodesToSave]);
                    if (lessons.length > 0)
                        setTimeout(pasteOneLesson, 10); // don't think this serialization is needed now
                    else {
                        // dialog.setShowAlert(false);
                        refreshLessonTree();
                        checkLessonTree('paste');
                    }
                };
                setTimeout(() => {
                    pasteOneLesson();
                }, 200);
            }
        }
    };

    const logout = () => {
         updateEditorState({ authenticated: false, showLogin: true });
         figma.call('setLoginCreds', { host: apiServer, creds: {} });
    };

    const updateFigmaTemplateCopy = async () => {
        dialog.setShowAlert(true, 'Getting latest templates, please wait...', 1000, true);
        await getFigmaTemplates(true);
        dialog.setShowAlert(false);
    };

    const newLessonFromTemplate = (lesson) => {

    };

    const newLesssonsSubMenus = () => [
        { label: 'Built-in lessons', submenu: lessonTypes.filter(t => t.type !== 'Additional pages')
            .map(t => ({ label: t.type, handler: addNode.bind(null, 'lesson', lessonTypeMap[t.type], editorState.lessonTree) }))},
        // { label: 'Template lessons', submenu: templateLessons.filter(lesson => !lesson.title.match(/page/i))
        //     .map(lesson => ({ label: lesson.title, handler: () => newLessonFromTemplate(lesson) })) },
    ];

    const headerMenu = () => {
        if (!templateDocs.templatePages)
            return [ { label: 'Please try again in a few seconds...', handler: () => {} } ];  // no menus until template docs are loaded
        getServerSideClipboard();
        return [
            // { label: 'Login',  handler: () => updateEditorState({ showLogin: true }) },
            // { label: 'Logout',  handler: logout },
            // { divider: true },
            { label: 'Add new lesson', submenu: newLesssonsSubMenus()},
            { divider: true },
            { label: 'Collapse tree',  handler: collapseNavigatorTree },
            { divider: true },
            // { label: 'Copy all lessons', handler: copyAllLessons },
            { label: 'Paste lesson', handler: pasteLessons },
            { label: 'Upload lesson', handler: importLesson },
            // { label: ' Backup all lessons', handler: backupAllLessons },
            // { label: 'Restore selected backups', handler: showRestoreDialog },
            { divider: true },
            { label: 'Get latest Figma templates',  handler: updateFigmaTemplateCopy },
            // { divider: true },
            // // { label: 'Backup', handler: backup },  deprecated in favor of per-lesson context-menu backup-via-dup
            // { label: 'Check lesson tree', handler: repairLessonTree },
            // { label: 'Conform to Figma layouts', handler: conformToFigmaLayout },
            // { label: 'Gather Figma assets', handler: gatherFigmaAssets },
        ];
    };

    const handleHeaderMenuSelect = (item) => {
        item.handler(item);
    };

    // ----------- drag-and-drop  ----

    const dragAndDropNode = (movingNode, toNode, place) => {
        const changedNodes = moveNode(movingNode, toNode, place);
        // update DB with moves
        const data = { data: { figmaFileKey: editorState.figmaFileKey, nodes: changedNodes.filter(n => n.nodeID !== 'root').map(n => ({ ...deflateNode(n) }))}};
        queueNodeMapUpdate(data);
        //
        refreshLessonTree();
        checkLessonTree('dragAndDropNode');
    };

    // --------- Figma interface, callbacks from figma via windows messaging ---

    const updateDBNodeMap = (updatedNodes, droppedNodes, options) => {
        const nodes =  updatedNodes.filter(n => n.nodeID !== 'root').map(n => ({ ...deflateNode(n) }));
        const deletedNodes =  droppedNodes && droppedNodes.filter(n => n.nodeID !== 'root').map(n => ({ ...deflateNode(n) }));
        const data = { data: { figmaFileKey: editorState.figmaFileKey, nodes, deletedNodes }};
        queueNodeMapUpdate(data, options);
    };

    const repairColor = (p) => { // repair incorrect color name  - remove this patcher after some time
        p.color = p.color === 'patterSubtitle' ? 'patternSubtitle' : p.color;
        return p;
    };

    // note that an editing as occured, for now recording logged-in user, should record edit history
    const wasEdited = (node) => {
        const lesson = lessonFor(node);
        if (lesson && editorState.user) {
            lesson.editedBy = editorState.user.id;
            updateDBNodeMap([lesson]);
        }
    };

    const getServerSideClipboard = () => {
        if (userContext.user) {
            httpAPI(apiServer, apiPaths.getClipboard, { data: { userID: userContext.user._id } } )
                .then(response => {
                    const obj = response.nodeStruct;
                    if (obj.root && (!editorState.clipboard || obj.timestamp > editorState.clipboardTimestamp)) {
                        wireNodeMap(obj.nodeMap);
                        const node = obj.nodeMap[obj.root];
                        node.parent = null;
                        updateEditorState({ clipboard: node, clipboardTimestamp: obj.timestamp });
                    }
                }).catch(err => { }); // ignore if no clipboard serverside
        }
    };

    const makePageURL = (lessonID, pageID) => `${lessonsPaths.player}/${lessonID}${pageID ? `/${pageID}` : ''}${global.showEditor ? '?edit=1' : ''}`;

    // EditorContext value
    return {
        ...editorState,
        update: updateEditorState,
        updateNodeField,
        updateElementField,
        editElementFields,
        updateElementFieldPart,
        updateElementFieldParts,
        updateElementListField,
        updateElementListFieldItem,
        fieldValue,
        commitNodeUpdate,
        refreshLessonTree,
        deleteConfirmed,
        clear,
        repairColor,
        queueNodeMapUpdate,
        updateDBNodeMap,
        headerMenu,
        handleHeaderMenuSelect,
        restoreBackups,
        endEditLesson,
        setUpdatePlayerOnEdit: (onOff) => updatePlayerOnEdit.current = onOff,
        setNavigate: (n) => navigate = n,
        setDialogContext: (d) => dialog = d,

        contextMenu,
        typeChoices,
        handleContextMenuSelect,
        disableContextMenu,
        closeTreeContextMenu,
        makePageURL,

        dragAndDropNode,
        checkLessonTree,
    };
};

export default EditorContext;